import { useCallback, useEffect, useRef, useState } from 'react'
import { BoxTruckBidder } from '@numeo/types'
import { BoxTruckBid, fetchBoxTruckBidders, fetchBoxTruckBids } from '../services/boxTruckLoadsApi'

/** Server page size for the Bidding list (mirrors the endpoint's default limit). */
export const BIDDING_PAGE_SIZE = 50

interface UseBoxTruckBids {
    bids: BoxTruckBid[]
    total: number
    /** Current 1-based page. */
    page: number
    loading: boolean
    error: string | null
    lastUpdatedAt: Date | null
    /** Re-fetch the current view from page 1. */
    refresh: () => void
    /** Jump to a page. */
    goToPage: (page: number) => void
    /** Teammates who have sent a bid in this project — the dispatcher filter's options. */
    dispatchers: BoxTruckBidder[]
    /** The dispatcher currently filtered on, or null for the whole team. */
    dispatcherId: string | null
    /** Filter to one dispatcher's bids (null = everyone). Refetches from page 1, since the server does the
     *  filtering — a dispatcher's older bids live on pages the client never fetched. */
    setDispatcherId: (id: string | null) => void
}

/** Loads the Bidding list — SYLECTUS_EMAIL loads in this project that already have an email thread. */
export const useBoxTruckBids = (): UseBoxTruckBids => {
    const [bids, setBids] = useState<BoxTruckBid[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null)
    const [dispatchers, setDispatchers] = useState<BoxTruckBidder[]>([])
    const [dispatcherId, setDispatcherIdState] = useState<string | null>(null)
    // Read inside the loader instead of closing over it, so `load` keeps a stable identity and the
    // mount effect below does not re-run (and double-fetch page 1) on every filter change.
    const dispatcherIdRef = useRef<string | null>(null)
    // Lets a completed fetch re-invoke the latest loader (snap-to-page-1 on an empty later page).
    const loadRef = useRef<((targetPage: number) => Promise<void>) | null>(null)

    const load = useCallback(async (targetPage: number) => {
        setLoading(true)
        setError(null)
        try {
            const { bids: items, total: count } = await fetchBoxTruckBids(targetPage, BIDDING_PAGE_SIZE, dispatcherIdRef.current ?? undefined)
            // An empty later page means the set shrank under us (bids aged out / were removed) — snap back
            // to page 1 rather than stranding the user on a dead-end page with a stale pager count.
            if (items.length === 0 && targetPage > 1) {
                void loadRef.current?.(1)
                return
            }
            setBids(items)
            setPage(targetPage)
            // The server only computes the grand total on page 1 (later pages skip the COUNT scan), so
            // adopt it there and keep it as the page-count denominator while navigating.
            if (targetPage === 1) setTotal(count)
            setLastUpdatedAt(new Date())
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load bids')
        } finally {
            setLoading(false)
        }
    }, [])
    loadRef.current = load

    const refresh = useCallback(() => void load(1), [load])
    const goToPage = useCallback((next: number) => void load(next), [load])
    const setDispatcherId = useCallback(
        (id: string | null) => {
            dispatcherIdRef.current = id
            setDispatcherIdState(id)
            // Page 1, not the current page: a narrower filter usually has fewer pages than the one being
            // viewed, and landing on a now-empty page 4 reads as "this dispatcher has no bids".
            void load(1)
        },
        [load]
    )

    useEffect(() => {
        void load(1)
    }, [load])

    // The filter's options are the whole team's bidders, independent of the current page or filter, so they
    // are fetched once. Best-effort: a failure just leaves the picker empty.
    useEffect(() => {
        void fetchBoxTruckBidders().then(setDispatchers)
    }, [])

    return { bids, total, page, loading, error, lastUpdatedAt, refresh, goToPage, dispatchers, dispatcherId, setDispatcherId }
}
